import { useState } from 'react';
import { Optional } from '../../../../Common/interfaces';
import { Switch } from '../../../../components';
import { QuestionView } from '../../../logbook/views/QuestionView';
import {
    Card,
    CardType,
    Exercise,
    FloatingButton,
    LabelBody,
    LabelBodyHeadline,
    LabelBodySmall,
    LabelDisplay2,
    LabelTelescope,
    ProgressBar,
    SecondaryButton,
    Spacer,
    SwirlyBox,
} from '../../components';
import { ExerciseData, ExerciseViewProps } from '../../exercises';
import {
    AddIcon,
    IllustrationBulbDiamond,
    IllustrationSignalDiamond,
    IllustrationThinkingHappy,
    PencilIcon,
} from '../../icons';
import { ExerciseStatus } from '../../interfaces';
import { normalizeInput } from '../../useSyncedState';
import {
    Module2Exercise1Popup,
    data as areaData,
} from './Module2Exercise1Popup';

const page3ThoughtsTextKey = 'module2Exercise1Page3ThoughtsText';
const page3FeelingsTextKey = 'module2Exercise1Page3FeelingsText';
const page3BodyTextKey = 'module2Exercise1Page3BodyText';
const page3BehaviourTextKey = 'module2Exercise1Page3BehaviourText';

type AreaKey = 'Tanker' | 'Følelser' | 'Krop' | 'Adfærd';

function getDataKeyForArea(key: AreaKey): string {
    switch (key) {
        case 'Tanker':
            return page3ThoughtsTextKey;
        case 'Følelser':
            return page3FeelingsTextKey;
        case 'Krop':
            return page3BodyTextKey;
        case 'Adfærd':
            return page3BehaviourTextKey;
    }
}

export const exerciseData21: ExerciseData = {
    ExerciseView: Module2Exercise1,
    dataKeys: [
        page3ThoughtsTextKey,
        page3FeelingsTextKey,
        page3BodyTextKey,
        page3BehaviourTextKey,
    ],
    questions: [
        {
            Answers: (props) => (
                <QuestionView
                    {...props}
                    title="Hvad tænkte du i situationen?"
                    description="Forsøg at indkredse dine tanker i situationen. Tanker kan f.eks. være om dig selv og din indsats, eller sammenligninger med andre."
                    answerTitle="Dine tanker"
                />
            ),
            dataKeys: [page3ThoughtsTextKey],
        },
        {
            Answers: (props) => (
                <QuestionView
                    {...props}
                    title="Forsøg at indkredse dine følelser i situationen"
                    description="Tanker og følelser kan være svære at skille ad. Men forsøg at finde din følelsesmæssig reaktion på en tanke. Fx kan tanken være, at jeg ikke passer ind, og følelsen er måske usikkerhed."
                    answerTitle="Dine følelser"
                />
            ),
            dataKeys: [page3FeelingsTextKey],
        },
        {
            Answers: (props) => (
                <QuestionView
                    {...props}
                    title="Hvordan reagerer din krop i den pressede situation?"
                    description="For mange er maven et kropsligt centrum for en psykisk reaktion. Mærker du mon noget der eller et andet sted i kroppen?"
                    answerTitle="Dine kropslige reaktioner"
                />
            ),
            dataKeys: [page3BodyTextKey],
        },
        {
            Answers: (props) => (
                <QuestionView
                    {...props}
                    title="Hvad gjorde du i situationen?"
                    description="Skriv din adfærd ned."
                    answerTitle="Din adfærd"
                />
            ),
            dataKeys: [page3BehaviourTextKey],
        },
    ],
};

function Module2Exercise1({ exercise, onContinue }: ExerciseViewProps) {
    return (
        <Exercise title="Hvad er dine faresignaler?">
            <ProgressBar current={exercise.CurrentPage} total={4} />
            <Switch
                cases={{
                    1: (
                        <Page1
                            onContinue={() => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 2,
                                });
                            }}
                        />
                    ),
                    2: (
                        <Page2
                            onContinue={() => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 3,
                                });
                            }}
                        />
                    ),
                    3: (
                        <Page3
                            thoughtsText={
                                exercise.Data[page3ThoughtsTextKey] ?? ''
                            }
                            feelingsText={
                                exercise.Data[page3FeelingsTextKey] ?? ''
                            }
                            bodyText={exercise.Data[page3BodyTextKey] ?? ''}
                            behaviourText={
                                exercise.Data[page3BehaviourTextKey] ?? ''
                            }
                            onSaveText={({ key, value }) =>
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: null,
                                    data: {
                                        [getDataKeyForArea(key)]: value,
                                    },
                                })
                            }
                            onContinue={() => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 4,
                                });
                            }}
                        />
                    ),
                    4: (
                        <Page4
                            onContinue={() => {
                                onContinue({
                                    status: ExerciseStatus.Completed,
                                    goToNext: false,
                                });
                            }}
                        />
                    ),
                }}
                value={exercise.CurrentPage}
                default={1}
            />
        </Exercise>
    );
}

type PageProps = { onContinue: () => void };
function Page1({ onContinue }: PageProps) {
    return (
        <>
            <LabelDisplay2>Kortlæg dine faresignaler</LabelDisplay2>
            <LabelBody class="mt-10">
                I pressede situationer kan vi ofte komme til at fokusere mest på
                vores tanker og følelser, men kroppen sender også vigtige
                signaler, som vi måske ikke altid lægger mærker til.
            </LabelBody>
            <IllustrationSignalDiamond className="mx-auto my-30" />
            <LabelDisplay2>Den kognitive diamant</LabelDisplay2>
            <LabelBody class="mt-10">
                Den kognitive diamant er et redskab til at undersøge
                sammenhængen mellem vores tanker, følelser, krop og adfærd, og
                hvordan de påvirker hinanden. Med hjælp fra denne model skal du
                forsøge at indkredse din krops faresignaler, i en presset
                situation.
            </LabelBody>
            <Card className="mx-auto my-30" type={CardType.Grey}>
                <img
                    className="mx-auto rounded-lg"
                    src={
                        '/images/icons_shape/Module5S1Exercise2Illustration2.png'
                    }
                    alt=""
                />
                <LabelBodySmall class="mt-10">
                    Billedet er illustrativt
                </LabelBodySmall>
                <LabelDisplay2 class="mb-10 mt-20">Et eksempel</LabelDisplay2>
                <LabelBody>
                    Anna studerer på universitetet. Hun har høje ambitioner for
                    sit karaktergennemsnit, samtidig med, at hun er optaget af
                    at være en god ven, datter og kæreste. Anna bliver ofte ramt
                    af presset i weekenden, hvor hun føler, at der er meget hun
                    skal nå, og at tiden ikke rækker. Her bliver hun ramt af{' '}
                    <b>tanker</b> som: “Jeg føler mig utilstrækkelig”. Annas{' '}
                    <b>krop</b> reagerer ved at hun får ondt i maven. Hun{' '}
                    <b>føler</b> sig nervøs og nedtrykt. Og Annas <b>adfærd</b>{' '}
                    bliver at skynde sig med alt hun vil i weekenden, og
                    efterlader hende træt og udkørt søndag aften.
                </LabelBody>
            </Card>
            <Spacer />
            <SecondaryButton onClick={onContinue}>
                Gå videre til øvelsen
            </SecondaryButton>
        </>
    );
}

function Page2({ onContinue }: PageProps) {
    return (
        <>
            <LabelDisplay2>Vælg en situation</LabelDisplay2>
            <LabelBody class="my-10">
                Du skal nu til at bruge diamanten på en situation i dit eget
                liv. Start med at tænke på en situation, hvor du følte dig
                presset. Vælg en situation, der ikke er alt for overvældende,
                men hvor du følte dig 5 eller 6 ud af 10 presset. F.eks. før en
                eksamen eller en anden vigtig begivenhed.
            </LabelBody>
            <IllustrationThinkingHappy className="mx-auto" />
            <SwirlyBox class="my-30">
                Når du har valgt en situation, kan du gå videre.
            </SwirlyBox>
            <Spacer />
            <SecondaryButton class="mt-30" onClick={onContinue}>
                Fortsæt øvelsen
            </SecondaryButton>
        </>
    );
}

type Page3Props = {
    thoughtsText: string;
    feelingsText: string;
    bodyText: string;
    behaviourText: string;
    onSaveText: (data: { key: AreaKey; value: string }) => void;
    onContinue: () => void;
};
function Page3({
    thoughtsText,
    feelingsText,
    bodyText,
    behaviourText,
    onSaveText,
    onContinue,
}: Page3Props) {
    const [showPopup, setShowPopup] = useState<Optional<AreaKey>>();

    const areas: { key: AreaKey; value: string }[] = [
        { key: 'Tanker', value: thoughtsText },
        { key: 'Krop', value: bodyText },
        { key: 'Følelser', value: feelingsText },
        { key: 'Adfærd', value: behaviourText },
    ];

    return (
        <>
            {showPopup && (
                <Module2Exercise1Popup
                    onClose={(text) => {
                        if (text != null) {
                            onSaveText({
                                key: showPopup,
                                value: normalizeInput(text),
                            });
                        }
                        setShowPopup(null);
                    }}
                    type={showPopup}
                    text={(() => {
                        switch (showPopup) {
                            case 'Tanker':
                                return thoughtsText;
                            case 'Følelser':
                                return feelingsText;
                            case 'Krop':
                                return bodyText;
                            case 'Adfærd':
                                return behaviourText;
                        }
                    })()}
                />
            )}
            <Card>
                <LabelDisplay2>Hvad mærker du?</LabelDisplay2>
                <LabelBody class="mt-10">
                    Hold situationen i din bevidsthed og besvar spørgsmålene:
                </LabelBody>
                <LabelBodyHeadline class="mt-10">
                    Hvad følte du i situationen?
                </LabelBodyHeadline>
                <LabelBody>F.eks. ked af det, frustreret, ængstelig.</LabelBody>
                <LabelBodyHeadline class="mt-10">
                    Hvad tænkte du i situationen?
                </LabelBodyHeadline>
                <LabelBody>F.eks. at du skal tage dig sammen.</LabelBody>
                <LabelBodyHeadline class="mt-10">
                    Hvordan mærker du det kropsligt?
                </LabelBodyHeadline>
                <LabelBody>F.eks. i maven, i brystet.</LabelBody>
                <LabelBodyHeadline class="mt-10">
                    Hvad var din adfærd i situationen?
                </LabelBodyHeadline>
                <LabelBody>
                    F.eks. blev ved med at gøre det du havde gang i,
                    doomscrollede.
                </LabelBody>
            </Card>
            <IllustrationSignalDiamond className="mx-auto my-30" />
            <LabelDisplay2>Reflekter og skriv ned</LabelDisplay2>
            <LabelBody class="my-10">
                Det er en god idé at bruge lidt tid på denne øvelse. Træk vejret
                og mærk situationen.
            </LabelBody>
            {areas.map(
                ({ key, value }) =>
                    value && (
                        <Card
                            key={key}
                            class="mt-20 cursor-pointer"
                            onClick={() => setShowPopup(key)}
                        >
                            <div className="flex flex-row justify-between">
                                <LabelBodyHeadline color="text-dark-red">
                                    {areaData[key].Subtitle}
                                </LabelBodyHeadline>
                                <PencilIcon className="cursor-pointer" />
                            </div>
                            <LabelTelescope>{value}</LabelTelescope>
                        </Card>
                    ),
            )}

            <div className="inline-flex flex-wrap">
                {areas.map(
                    ({ key, value }) =>
                        !value && (
                            <FloatingButton
                                key={key}
                                icon={<AddIcon />}
                                text={key}
                                className="mr-20 mt-20"
                                onClick={() => setShowPopup(key)}
                            />
                        ),
                )}
            </div>
            <SecondaryButton class="mt-30" onClick={onContinue}>
                Fortsæt
            </SecondaryButton>
        </>
    );
}

function Page4({ onContinue }: PageProps) {
    return (
        <>
            <LabelDisplay2>Et godt redskab</LabelDisplay2>
            <LabelBody class="my-10">
                Du er nu begyndt at indkredse dine faresignaler på at føle dig
                presset.
            </LabelBody>
            <LabelBody>
                Det er ikke sikkert, at signalerne altid er det samme. Hvis du
                har lyst, kan du bruge den kognitive diamant næste gang, du er i
                en presset situation. Læg mærke til hvad du tænker, føler,
                mærker i kroppen og hvordan du handler og forsøg at blive
                klogere på; hvad sker der lige nu? Over tid kan du måske begynde
                at se et mønster.
            </LabelBody>
            <IllustrationBulbDiamond className="mx-auto my-30" />
            <Spacer />
            <SecondaryButton onClick={onContinue}>Afslut</SecondaryButton>
        </>
    );
}
